import { makeObservable } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import { audioRecordingModule } from "@/modules/audio-recording";
import { TranscriptionStatus } from "@/store/audio/recording";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  AudioRecordingTranscriptState,
  AudioRecordingTranscriptStatus,
  IAudioTranscriptionStore,
  TRANSCRIPTION_COMPROMISED_ERROR_MESSAGE,
} from "@/store/audio/transcription/audio-transcription.types";
import type { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";

export class AppStoreAudioTranscriptionStore
  extends AppSubStore
  implements IAudioTranscriptionStore
{
  private readonly localAudioRecordingTranscriptTurns: LocalAudioRecordingTranscriptTurnsRepository;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.localAudioRecordingTranscriptTurns =
      injectedDeps.store.dexie.repositories.localAudioRecordingTranscriptTurns;

    makeObservable<AppStoreAudioTranscriptionStore, "localAudioRecordingTranscriptTurns">(this, {
      localAudioRecordingTranscriptTurns: false,
      getTranscriptState: false,
      clearTranscriptionStatus: false,
    });
  }

  async getTranscriptState({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<AudioRecordingTranscriptState> {
    const transcriptionStatus = this.store.audio.recording.getTranscriptionStatus(audioRecordingId);

    if (transcriptionStatus === TranscriptionStatus.Compromised) {
      return {
        status: AudioRecordingTranscriptStatus.Compromised,
        content: "",
        transcriptArray: [],
        errorMessage: TRANSCRIPTION_COMPROMISED_ERROR_MESSAGE,
      };
    }

    const transcriptTurns = await this.localAudioRecordingTranscriptTurns.getAllByRecordingId({
      associatedAudioRecordingId: audioRecordingId,
    });
    const { transcriptText, transcriptArray } =
      audioRecordingModule.buildTranscriptPayload(transcriptTurns);

    if (!transcriptText.trim()) {
      return {
        status: AudioRecordingTranscriptStatus.Missing,
        content: transcriptText,
        transcriptArray: [],
        errorMessage: "No transcript available",
      };
    }

    return {
      status: AudioRecordingTranscriptStatus.Ready,
      content: transcriptText,
      transcriptArray,
      errorMessage: null,
    };
  }

  clearTranscriptionStatus({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.store.audio.recording.clearTranscriptionStatus(audioRecordingId);
  }
}
